import { Typography } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import classNames from 'classnames';
import React from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  MessageDescriptor,
  useIntl,
} from 'react-intl';

import ruleMessages from 'containers/ApprovalRules/messages';

import { LEVEL_PRESENTATION, PillTone } from './levelPresentation';
import {
  ApproverState,
  approverState,
  LevelState,
  levelProgress,
  levelState,
  visibleApprovers,
} from './levelState';
import messages from './messages';
import { useStyles } from './styles';
import { ChainApprover, ChainLevel, ChainRuleGroup } from './types';

interface ApprovalLevelChainProps {
  rules: ChainRuleGroup[];
  /**
   * Whether the request has finished. A level's own status cannot say: a
   * rejection leaves later levels `pending` and a cancellation leaves an open
   * one `active`, so without this the chain reports both as though the request
   * were still running. See `levelState`.
   */
  requestSettled: boolean;
  /**
   * Names each rule above its levels. Off by default: with one rule the name is
   * a heading over the whole chain that repeats what the chain already is, which
   * `content-hierarchy` calls adding a level of hierarchy because space exists
   * for it. On by the caller when several rules gate the subject — since
   * AXHE-4183 each advances its own ladder, so two can both sit at "Level 1".
   */
  showRuleNames?: boolean;
  /**
   * IANA zone the timestamps render in. Undefined leaves them on the browser's,
   * which is what a caller that has no better answer should pass.
   *
   * A zone rather than pre-formatted strings, because the caller cannot format
   * what it cannot see: which timestamps this renders, and beside which pill, is
   * this component's business. The caller only knows whose clock to use — the
   * signed-in user's for a recruiter reading the chain (`whoami.time_zone`), or
   * the opening's for a deadline that was computed against it.
   */
  timeZone?: string;
  /**
   * Drop the identity sub-lines under each name — the address, the role they
   * were resolved through, and the "Backup" tag — leaving the name and the
   * answer.
   *
   * Opt-in the way `showRuleNames` is, and for the same reason: only the caller
   * knows what its surface is for. Both offer rails want none of those lines —
   * the review panel's and the Send Offer drawer's read-only one. No design
   * source for either shows them, and at 240px three sub-lines per person push
   * the answers the rail exists for below the fold. The progress panel keeps
   * them.
   */
  identityOnly?: boolean;
  /**
   * Draw the rows the way the offer review panel's design does: each name at
   * the body2 size, a rejecting approver's reason at the overline size in
   * secondary text, no pill beside a level's name, and that rail's spacing
   * (`styles.ts`).
   *
   * The level's date stays, and once the pill is gone it is what says a level
   * has closed. So do the notes that say why a level closed or never applied:
   * they carry states that nothing else on the row would. A screen reader
   * still hears the level's own state, from text beside the name that only it
   * reads.
   */
  compact?: boolean;
}

const APPROVER_PILLS: Record<
  ApproverState,
  { label: MessageDescriptor; tone: PillTone; Icon: typeof ScheduleIcon }
> = {
  approved: {
    label: messages.approverApproved,
    tone: 'approved',
    Icon: CheckCircleOutlineIcon,
  },
  rejected: {
    label: messages.approverRejected,
    tone: 'rejected',
    Icon: HighlightOffIcon,
  },
  pending: {
    label: messages.approverPending,
    tone: 'neutral',
    Icon: ScheduleIcon,
  },
  cancelled: {
    label: messages.approverCancelled,
    tone: 'neutral',
    Icon: RemoveCircleOutlineIcon,
  },
};

/**
 * A word, a colour and a glyph. Colour alone is prohibited, and the word is what
 * carries the meaning for anybody who cannot separate the greens from the reds.
 * The glyph is decorative — the pill's own text already names the state.
 */
const Pill: React.FC<{
  label: MessageDescriptor;
  tone: PillTone;
  Icon: typeof ScheduleIcon;
  /**
   * Overrides what is announced, not what is shown. A level and an approver on it
   * can carry the same word — "Approved" beside "Approved" — which reads as one
   * ambiguous pair aloud even though the rows are visually distinct.
   */
  ariaLabel?: string;
}> = ({ label, tone, Icon, ariaLabel }) => {
  const styles = useStyles();
  const intl = useIntl();
  const toneClass = {
    approved: styles.pillApproved,
    rejected: styles.pillRejected,
    neutral: styles.pillNeutral,
  }[tone];

  return (
    <span aria-label={ariaLabel} className={classNames(styles.pill, toneClass)}>
      <Icon aria-hidden="true" className={styles.pillIcon} />
      {intl.formatMessage(label)}
    </span>
  );
};

/**
 * When something happened, on somebody's clock.
 *
 * Month spelled out: a numeric-only date is read day-first by most of the world
 * and month-first by the US, and an audit trail cannot afford the ambiguity.
 *
 * The zone is passed to `FormattedDate`, never left to the host. An unzoned date
 * is formatted on whatever clock the machine happens to hold, which agrees with
 * the intended zone on any developer or CI box sharing its offset and is wrong
 * by a day for everybody else. `null` renders nothing rather than an epoch.
 */
const ChainDate: React.FC<{
  value: string | null | undefined;
  timeZone?: string;
  /** Deadlines carry the hour; a deadline without one is not a deadline. */
  withTime?: boolean;
}> = ({ value, timeZone, withTime = false }) => {
  if (!value) return null;

  const at = new Date(value);

  return (
    <time dateTime={value}>
      <FormattedDate
        day="numeric"
        month="short"
        timeZone={timeZone}
        value={at}
        year="numeric"
      />
      {withTime && (
        <>
          {', '}
          <FormattedTime timeZone={timeZone} value={at} />
        </>
      )}
    </time>
  );
};

const ApproverRow: React.FC<{
  approver: ChainApprover;
  state: ApproverState;
  timeZone?: string;
  identityOnly?: boolean;
  compact?: boolean;
}> = ({ approver, state, timeZone, identityOnly = false, compact = false }) => {
  const styles = useStyles();
  const intl = useIntl();
  const pill = APPROVER_PILLS[state];
  // Only on a row that carries an answer. A `decidedAt` beside "Pending" would
  // be a timestamp for something that has not happened.
  const answeredAt =
    state === 'approved' || state === 'rejected' ? approver.decidedAt : null;

  return (
    <div>
      <div className={styles.approverRow}>
        <div className={styles.approverIdentity}>
          <Typography
            className={compact ? styles.approverNameCompact : undefined}
            // body2 is the secondary role and grey in this theme, but the name
            // is still what the row is about.
            color={compact ? 'textPrimary' : 'initial'}
            variant={compact ? 'body2' : 'body1'}
          >
            {/* An approver whose user record is gone still renders as a row: the
                level was asked of somebody, and blanking the row loses that from
                the audit trail. */}
            {approver.name ?? intl.formatMessage(messages.unnamedApprover)}
          </Typography>
          {!identityOnly && approver.email && (
            <Typography variant="body2">{approver.email}</Typography>
          )}
          {/* The named role this person was resolved through. The answer to
              "why them?", which is the likeliest support question this feature
              produces. */}
          {!identityOnly && approver.role && (
            <Typography className={styles.levelNote} variant="body2">
              {approver.role}
            </Typography>
          )}
          {!identityOnly && approver.backup && (
            <Typography className={styles.levelNote} variant="body2">
              <FormattedMessage {...messages.backupApprover} />
            </Typography>
          )}
        </div>
        <div className={styles.statusColumn}>
          <Pill label={pill.label} tone={pill.tone} Icon={pill.Icon} />
          {answeredAt && (
            <Typography className={styles.timestamp} variant="body2">
              <ChainDate timeZone={timeZone} value={answeredAt} />
            </Typography>
          )}
        </div>
      </div>
      {approver.comment && (
        <Typography
          className={classNames(
            styles.approverComment,
            compact && styles.approverCommentCompact,
          )}
          // overline renders a span, and the margin above the reason needs a
          // block.
          component="p"
          variant={compact ? 'overline' : 'body2'}
        >
          {approver.comment}
        </Typography>
      )}
    </div>
  );
};

const LevelApprovers: React.FC<{
  level: ChainLevel;
  state: LevelState;
  timeZone?: string;
  identityOnly?: boolean;
  compact?: boolean;
}> = ({ level, state, timeZone, identityOnly, compact }) => {
  const styles = useStyles();
  const approvers = visibleApprovers(level);

  if (approvers.length === 0) {
    return (
      <Typography className={styles.levelNote} variant="body2">
        <FormattedMessage {...messages.noApprovers} />
      </Typography>
    );
  }

  return (
    <>
      {/* Index in the key as well as the id: a row the server sent without a
          user id falls back to null, so two such rows would otherwise share a
          key and React could apply the first's DOM to the second's data — on an
          audit-facing list. */}
      {approvers.map((approver, index) => (
        <ApproverRow
          approver={approver}
          compact={compact}
          identityOnly={identityOnly}
          key={`${approver.id ?? 'unknown'}-${index}`}
          state={approverState(approver, state)}
          timeZone={timeZone}
        />
      ))}
    </>
  );
};

const LevelBlock: React.FC<{
  level: ChainLevel;
  requestSettled: boolean;
  timeZone?: string;
  identityOnly?: boolean;
  compact?: boolean;
}> = ({ level, requestSettled, timeZone, identityOnly, compact }) => {
  const styles = useStyles();
  const intl = useIntl();
  const state = levelState(level, requestSettled);
  const pill = LEVEL_PRESENTATION[state];
  const progress = levelProgress(level, requestSettled);
  // Two different reasons a level closed with nobody asked, and they are not
  // interchangeable — see `levelState`.
  const closedNote =
    state === 'notTriggered'
      ? messages.levelNotTriggeredNote
      : state === 'cancelled'
        ? messages.levelCancelledNote
        : null;

  return (
    <div className={classNames(styles.level, compact && styles.levelCompact)}>
      <div
        className={classNames(
          styles.levelHeadingRow,
          compact && styles.levelHeadingRowCompact,
        )}
      >
        {/* Uppercased by the stylesheet, not by the string. The label comes from
            the rule editor's catalog — a level is "Level 2 · Finance review"
            there, and one sentence for one idea means it is the same here. */}
        <div
          className={classNames(
            styles.levelHeading,
            compact && styles.compactWrap,
          )}
        >
          {level.name ? (
            <FormattedMessage
              {...ruleMessages.levelRowNamed}
              values={{ number: level.position, name: level.name }}
            />
          ) : (
            <FormattedMessage
              {...ruleMessages.levelRow}
              values={{ number: level.position }}
            />
          )}
        </div>
        {/* A compact chain draws no pill, but a screen reader reading in order
            would otherwise meet the level's name and a bare date with nothing
            saying what the level came to. */}
        {compact && (
          <span className={styles.visuallyHidden}>
            {intl.formatMessage(messages.levelStateLabel, {
              state: intl.formatMessage(pill.label),
            })}
          </span>
        )}
        {/* The level's own answer, beside its name. Without it a level reading
            "Approved / Pending" is ambiguous: on `any` logic it is finished and
            the second person will never be asked, on `all` it is blocked on
            them. A compact chain leaves that to the date, so an open level
            gets no column and its name takes the whole row. */}
        {(!compact || level.decidedAt) && (
          <div className={styles.statusColumn}>
            {!compact && (
              <Pill
                ariaLabel={intl.formatMessage(messages.levelStateLabel, {
                  state: intl.formatMessage(pill.label),
                })}
                label={pill.label}
                tone={pill.tone}
                Icon={pill.Icon}
              />
            )}
            {/* When the level itself settled, which is not the same as when
                any one approver answered: under `all` logic the level closes
                on the last of them, and under `any` on the first. */}
            {level.decidedAt && (
              <Typography className={styles.timestamp} variant="body2">
                <ChainDate timeZone={timeZone} value={level.decidedAt} />
              </Typography>
            )}
          </div>
        )}
      </div>

      {progress && (
        <Typography className={styles.levelNote} variant="body2">
          <FormattedMessage {...messages.levelProgress} values={progress} />
        </Typography>
      )}

      {closedNote ? (
        <Typography className={styles.levelNote} variant="body2">
          <FormattedMessage {...closedNote} />
        </Typography>
      ) : (
        <LevelApprovers
          compact={compact}
          identityOnly={identityOnly}
          level={level}
          state={state}
          timeZone={timeZone}
        />
      )}

      {level.needsAttention && (
        <div className={styles.levelWarning} role="alert">
          <ErrorOutlineIcon aria-hidden="true" className={styles.warningIcon} />
          <Typography color="inherit" variant="body2">
            <FormattedMessage {...messages.levelNeedsAttention} />
          </Typography>
        </div>
      )}

      {/* Why this level is open at all, when the answer is not "its condition
          matched" (AXHE-4504). */}
      {level.conditionUnevaluatable && (
        <Typography className={styles.levelNote} variant="body2">
          <FormattedMessage {...messages.levelHeld} />
        </Typography>
      )}

      {level.escalated && (
        <Typography className={styles.levelNote} variant="body2">
          <FormattedMessage {...messages.levelEscalated} />
        </Typography>
      )}

      {/* The deadline, on the only level that can still meet it. A level that
          has escalated says so above, and one that has closed is not waiting on
          anybody — printing a future deadline on either states an intent the
          engine has already discharged. */}
      {state === 'active' && !level.escalated && level.escalateAt && (
        <Typography className={styles.levelNote} variant="body2">
          <FormattedMessage
            {...messages.levelEscalatesAt}
            values={{
              date: (
                <ChainDate
                  timeZone={timeZone}
                  value={level.escalateAt}
                  withTime
                />
              ),
            }}
          />
        </Typography>
      )}
    </div>
  );
};

/**
 * Who has to sign a subject off, and where each of them is up to.
 *
 * Grouped rule → level → approver, which is the shape the engine runs: since
 * AXHE-4183 each matching rule advances its own ladder, so two rules can both be
 * sitting on their own "Level 1". A flat list would show that number twice with
 * nothing saying they are different ladders.
 *
 * Shared because the chain is ratified in two places and rendered in three —
 * Design Requirements §6 (the offer decision dialog's sidebar, AXHE-3947), §10
 * (the recruiter read-only view, AXHE-3912) and AXHE-3948's progress panel. The
 * state derivation in `levelState.ts` is the part that must not fork.
 */
export const ApprovalLevelChain: React.FC<ApprovalLevelChainProps> = ({
  rules,
  requestSettled,
  showRuleNames = false,
  timeZone,
  identityOnly = false,
  compact = false,
}) => {
  const styles = useStyles();

  return (
    <div className={classNames(styles.chain, compact && styles.chainCompact)}>
      {rules.map(rule => (
        <div
          className={classNames(
            styles.ruleGroup,
            compact && styles.ruleGroupCompact,
          )}
          key={rule.id ?? 'unattributed'}
        >
          {/* Absent when the snapshot could not attribute the levels to a rule —
              a request written before the engine recorded which rule produced a
              level. The levels still render; naming the group "unknown" would
              tell a reader nothing they can act on. */}
          {showRuleNames && rule.name && (
            <div
              className={classNames(
                styles.ruleName,
                compact && styles.compactWrap,
              )}
            >
              {rule.name}
            </div>
          )}
          {rule.levels.map((level, index) => (
            <LevelBlock
              compact={compact}
              identityOnly={identityOnly}
              key={`${level.stepId}-${index}`}
              level={level}
              requestSettled={requestSettled}
              timeZone={timeZone}
            />
          ))}
        </div>
      ))}
    </div>
  );
};
